import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';

/**
 * AgentsSection - Phase 3 of the homepage rewrite.
 *
 * Renders a "Set rules, the agents watch the market" pitch with a static
 * RULE -> EVAL -> ALERT flow graphic. Primary CTA is auth-aware:
 *   - Authenticated: navigate into the app with `?tab=agents` (App.tsx
 *     parses the query param on mount and sets activeTab).
 *   - Anonymous: no mid-page sign-in CTA — visitors convert via the
 *     header Log in, hero trial, pricing, or final CTA instead.
 *
 * Secondary CTA scrolls to the feature grid (#features).
 *
 * i18n: keys live under `landing.agentsBlock.*` (already in en.json).
 * English fallbacks are passed inline via `t(key, { defaultValue })` so
 * the component still renders correctly if a locale bundle is missing
 * the section.
 */
const AgentsSection: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const handlePrimary = () => {
    // Only rendered for authenticated users (anon converts via the header /
    // hero / pricing CTAs, not a mid-page sign-in button). Full reload to
    // /?tab=agents so AuthGate boots App fresh and the tab-query parser in
    // App.tsx (Phase 3) picks it up on mount.
    window.location.href = '/?tab=agents';
  };

  const handleSecondary = () => {
    // "Learn more" scrolls to the feature grid. The Help Center modal is not
    // mounted on the public landing page, so dispatching openHelpCenter here
    // did nothing - scroll to #features, which is always present.
    document
      .getElementById('features')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="agents"
      className="landing-section py-4 py-md-5"
      aria-labelledby="agents-headline"
      data-testid="agents-section"
    >
      <div className="container">
        <div className="row align-items-center g-3 g-md-4">
          <div className="col-12 col-lg-6">
            <p
              className="landing-eyebrow mb-2"
              style={{ color: 'var(--accent-primary)' }}
            >
              {t('landing.agentsBlock.eyebrow', {
                defaultValue: 'Autonomous agents',
              })}
              {' '}
              <span className="text-xs font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded align-middle" style={{ backgroundColor: 'var(--accent-primary-muted)', color: 'var(--accent-primary)' }}>
                {t('landing.agentsBlock.betaBadge', { defaultValue: 'Beta' })}
              </span>
            </p>
            <h2
              id="agents-headline"
              className="landing-section__h2 fw-semibold mb-3"
              style={{
                fontFamily: 'var(--font-display)',
                color: 'var(--text-primary)',
              }}
            >
              {t('landing.agentsBlock.headline', {
                defaultValue: 'Set rules. The agents watch the market.',
              })}
            </h2>

            <ul className="list-unstyled mb-4 d-flex flex-column gap-2">
              {(['bullet1', 'bullet2', 'bullet3'] as const).map((bk, i) => (
                <li
                  key={bk}
                  data-testid={`agents-bullet-${i + 1}`}
                  className="d-flex align-items-start gap-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'var(--accent-primary)',
                      marginTop: 9,
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    {t(`landing.agentsBlock.${bk}`, {
                      defaultValue:
                        bk === 'bullet1'
                          ? 'Define rules from your filter presets and price levels.'
                          : bk === 'bullet2'
                            ? 'Continuous evaluation against fresh market data.'
                            : 'Email, Telegram, WhatsApp alerts when a rule fires.',
                    })}
                  </span>
                </li>
              ))}
            </ul>

            <div className="d-flex flex-wrap gap-2 gap-md-3">
              {isAuthenticated && (
                <button
                  type="button"
                  className="cta-button px-3 py-2 px-md-4 py-md-3"
                  onClick={handlePrimary}
                  data-testid="agents-cta-primary"
                >
                  <span className="d-flex align-items-center gap-2">
                    {t('landing.agentsBlock.ctaAuthed', {
                      defaultValue: 'Open Agent Console',
                    })}
                    <ArrowRight size={18} />
                  </span>
                </button>
              )}
              <button
                type="button"
                className="cta-button cta-button-secondary px-3 py-2 px-md-4 py-md-3"
                onClick={handleSecondary}
                data-testid="agents-cta-secondary"
              >
                <span>
                  {t('landing.agentsBlock.secondary', {
                    defaultValue: 'Learn more',
                  })}
                </span>
              </button>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div
              className="glass-card p-3 p-md-4"
              data-testid="agents-flow-graphic"
              style={{ borderRadius: 'var(--radius-xl, 12px)' }}
            >
              {/*
                Static RULE -> EVAL -> ALERT diagram. SVG is decorative;
                the surrounding card carries semantic meaning via the
                section heading and bullets above.
              */}
              <svg
                role="img"
                aria-label="Rule, Evaluate, Alert pipeline"
                viewBox="0 0 360 120"
                width="100%"
                height="120"
                style={{ display: 'block', height: 'auto' }}
              >
                <defs>
                  <marker
                    id="agents-arrow-head"
                    viewBox="0 0 10 10"
                    refX="8"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto-start-reverse"
                  >
                    <path
                      d="M 0 0 L 10 5 L 0 10 z"
                      fill="var(--accent-primary)"
                    />
                  </marker>
                </defs>

                {/* Three node groups */}
                {[
                  { x: 30, label: 'RULE' },
                  { x: 145, label: 'EVAL' },
                  { x: 260, label: 'ALERT' },
                ].map((node) => (
                  <g key={node.label}>
                    <rect
                      x={node.x}
                      y={35}
                      width={70}
                      height={50}
                      rx={8}
                      ry={8}
                      fill="var(--accent-primary-muted)"
                      stroke="var(--accent-primary)"
                      strokeOpacity={0.55}
                    />
                    <text
                      x={node.x + 35}
                      y={65}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontFamily="var(--font-mono, monospace)"
                      fontSize="13"
                      fontWeight="600"
                      fill="var(--accent-primary)"
                    >
                      {node.label}
                    </text>
                  </g>
                ))}

                {/* Connectors */}
                <line
                  x1={100}
                  y1={60}
                  x2={140}
                  y2={60}
                  stroke="var(--accent-primary)"
                  strokeWidth={2}
                  strokeOpacity={0.7}
                  markerEnd="url(#agents-arrow-head)"
                />
                <line
                  x1={215}
                  y1={60}
                  x2={255}
                  y2={60}
                  stroke="var(--accent-primary)"
                  strokeWidth={2}
                  strokeOpacity={0.7}
                  markerEnd="url(#agents-arrow-head)"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgentsSection;
export { AgentsSection };
